import React from 'react';
import { Link } from 'react-router-dom';
import { getBrandingContext } from '../../../utils/hostname';
import './Footer.scss';

export const COMPANY = {
  name: 'Unique Service Partner',
  legalForm: 'Sp. z o.o.',
  street: 'ul. Obrzeżna 7',
  city: '02-691 Warszawa',
  phone: '22 417 34 40',
  phoneHref: 'tel:+48224173440',
  hours: 'Pn–Pt 8:00–16:00',
  krs: '0000634003',
  nip: '525-267-38-99',
  regon: '365261002'
};

const getContactEmail = ({ isOrange, isInsurances, isNais }) => {
  if (isOrange) return 'orange@uspartner.pl';
  if (isInsurances) return 'ubezpieczenia@uspartner.pl';
  if (isNais) return 'nais@uspartner.pl';
  return 'tvpubezpieczenia@uspartner.pl';
};

const getTagline = ({ isOrange, isInsurances, isNais }) => {
  if (isOrange) {
    return [
      'Ubezpieczenia dla pracowników Orange',
      'i pełne wsparcie, gdy coś się stanie.'
    ];
  }
  if (isNais) {
    return [
      'Ubezpieczenia dla użytkowników Nais',
      'i pełne wsparcie, gdy coś się stanie.'
    ];
  }
  if (isInsurances) {
    return [
      'Ubezpieczenia dla Ciebie',
      'i pełne wsparcie, gdy coś się stanie.'
    ];
  }
  return [
    'Ubezpieczenia dla pracowników i współpracowników TVP',
    'oraz pełne wsparcie, gdy coś się stanie.'
  ];
};

const getRegulaminHref = ({ isOrange, isInsurances, isNais }) => {
  if (isNais) return '/public-assets/USP_regulamin_serwisu_Nais.pdf';
  if (isOrange || isInsurances) return null;
  return '/public-assets/USP_regulamin_serwisu%20TVP%2015.04.2019.pdf';
};

const getOfferLinks = (branding) => {
  const {
    isOrange,
    isNais,
    showFullDashboard,
    showShooterProtect,
    showRowerzysta
  } = branding;

  return [
    showFullDashboard && { label: 'Opieka szpitalna', to: '/pracownicy/opis/' },
    { label: 'Podróże zagraniczne', to: '/zagraniczne/opis/' },
    { label: 'Moto Assistance', to: '/assistance/opis/' },
    { label: 'Ubezpieczenie auta', to: '/komunikacyjne/opis/' },
    { label: 'Majątkowe', to: '/majatkowe/opis/' },
    !isOrange && !isNais && { label: 'Pethelp', to: '/pethelp/opis/' },
    showRowerzysta && { label: 'Rowerzysta', to: '/rowerzysta/opis/' },
    { label: 'NNW szkolne', to: '/edu/opis/' },
    showShooterProtect && {
      label: 'ShooterProtect',
      href: 'https://shooterprotect.pl/'
    },
    { label: 'Zgłoszenie szkody', to: '/likwidacja/opis/' }
  ].filter(Boolean);
};

const FooterLink = ({ to, href, children }) =>
  href ? (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ) : (
    <Link to={to}>{children}</Link>
  );

const Footer = () => {
  const branding = getBrandingContext();
  const email = getContactEmail(branding);
  const regulaminHref = getRegulaminHref(branding);
  const offerLinks = getOfferLinks(branding);
  const [taglineLead, taglineRest] = getTagline(branding);
  return (
    <footer className="footer">
      <div className="fixedWidth footerIn">
        <div className="footerBrand">
          <img
            className="footerLogo"
            src={`${process.env.PUBLIC_URL}/tvp/logo-usp-light.svg`}
            alt="USP — Unique Service Partner"
            width="190"
            height="83"
          />
          <p className="footerTagline">
            <span className="footerTaglineLead">{taglineLead}</span>{' '}
            {taglineRest}
          </p>
          <a className="footerPhone" href={COMPANY.phoneHref}>
            {COMPANY.phone}
          </a>
          <span className="footerHours">{COMPANY.hours}</span>
        </div>

        <div className="footerCols">
          <div className="footerCol footerCol--offer">
            <h3>Oferta</h3>
            <ul
              className="footerOfferList"
              style={{
                gridTemplateRows: `repeat(${Math.ceil(offerLinks.length / 2)}, auto)`
              }}
            >
              {offerLinks.map((link) => (
                <li key={link.label}>
                  <FooterLink to={link.to} href={link.href}>
                    {link.label}
                  </FooterLink>
                </li>
              ))}
            </ul>
          </div>

          <div className="footerCol">
            <h3>Firma</h3>
            <ul>
              <li>
                <FooterLink href="https://uspartner.pl">O nas</FooterLink>
              </li>
              <li>
                <FooterLink href="https://uspartner.pl/pl/likwidacja-szkod">
                  Likwidacja szkód
                </FooterLink>
              </li>
              <li>
                <Link to="/kontakt">Kontakt</Link>
              </li>
              {regulaminHref && (
                <li>
                  <FooterLink href={regulaminHref}>
                    Regulamin serwisu
                  </FooterLink>
                </li>
              )}
              <li>
                <FooterLink href="https://uspartner.pl/pl/polityka-prywatnosci">
                  Polityka prywatności
                </FooterLink>
              </li>
            </ul>
          </div>

          <div className="footerCol">
            <h3>Kontakt</h3>
            <address className="footerAddr">
              {COMPANY.name}
              <br />
              {COMPANY.legalForm}
              <br />
              {COMPANY.street}
              <br />
              {COMPANY.city}
              <br />
              <a href={`mailto:${email}`}>{email}</a>
            </address>
          </div>
        </div>
      </div>

      <div className="fixedWidth footerLegal">
        <span>
          © {new Date().getFullYear()} USP. Wszelkie prawa zastrzeżone.
        </span>
        <span>
          KRS {COMPANY.krs} · NIP {COMPANY.nip} · REGON {COMPANY.regon}
        </span>
      </div>
    </footer>
  );
};

export default Footer;
